import { createContext, type ReactNode, useContext } from "react";
import {
  StyleSheet,
  Text as RNText,
  type TextProps as RNTextProps,
  type StyleProp,
  type TextStyle,
  useWindowDimensions,
  type AccessibilityRole,
  Platform,
} from "react-native";

import { useTheme } from "../ThemeProvider";
import { useTypographyDirection } from "../util/useTypographyDirection";
import { normalizeTextStyle } from "./normalizeTextStyle";
import { type TextLevel, getFontSizeStyle } from "./TextLevel";

export type TextRef = RNText;
export type TextVariant = "normal" | "contrast" | "secondary" | "alert" | "success";

const TextAncestorContext = createContext(false);

export type TextProps = {
  /**
   * Text level from 1 (smallest) to 8 (largest)
   */
  level?: TextLevel;

  /**
   * Overrides the semantic heading level (aria-level) independently of the
   * visual text size. When omitted, `aria-level` defaults to `level`.
   */
  ariaLevel?: number;

  /**
   * Change the style of the Text. Choose between `normal` (default), `contrast`, `secondary`, `alert`, and `success`.
   */
  variant?: TextVariant;

  /**
   * The text content of the link to render
   */
  children: ReactNode;

  /**
   * additional styles on Text component, please pass the general CSS
   */
  sx?: StyleProp<TextStyle>;

  /**
   * label to show to screen readers
   * This defaults to true on all platforms *except* Android.
   * On Android setting selectable to true will make the text focusable and add it to the keyboard focus order.
   * See A11Y-7173 for additional details (https://indeed.atlassian.net/browse/A11Y-7173).
   */
  selectable?: boolean;

  /**
   * label to show to screen readers
   */
  ariaLabel?: string;

  /**
   * set to true to hide from screen readers
   */
  ariaHidden?: boolean;

  /**
   * Optional press handler for making text clickable
   */
  onPress?: () => void;

  /**
   * Native ID for accessibility label association (accessibilityLabelledBy)
   */
  nativeID?: string;

  /**
   * Whether this element is accessible to screen readers
   */
  accessible?: boolean;

  /**
   * Accessibility Role tells a person using either VoiceOver on iOS or TalkBack on Android the type of element that is focused on.
   */
  accessibilityRole?: AccessibilityRole;

  /**
   * Ref to the underlying RNText component
   */
  ref?: React.Ref<RNText>;
} & Pick<RNTextProps, "numberOfLines" | "onTextLayout" | "testID">;

export function Text(props: TextProps): React.JSX.Element {
  const theme = useTheme();
  const typography = useTypographyDirection();
  const hasTextAncestor = useContext(TextAncestorContext);

  const variants = {
    contrast: {
      color: theme.colors.foregroundStrong,
    },
    normal: {
      color: theme.colors.foreground,
    },
    secondary: {
      color: theme.colors.foregroundWeak,
    },
    alert: {
      color: theme.colors.foregroundStatusCritical,
    },
    success: {
      color: theme.colors.foregroundStatusSuccess,
    },
  };
  const { fontScale } = useWindowDimensions();
  const fontStyle =
    props.level !== undefined
      ? getFontSizeStyle(props.level, theme)
      : hasTextAncestor
        ? undefined
        : { fontSize: theme.fontSizes.f2 };
  const colorStyle = props.variant !== undefined ? variants[props.variant] : variants.normal;
  const baseTextStyle: TextStyle = {
    fontFamily: theme.fonts.default,
    alignSelf: "auto",
    // @ts-expect-error textAlign is a react-native-web prop not in RN type definitions
    textAlign: typography.textAlign,
    letterSpacing: fontScale * 0.0001, //Force a layout recalculation when font scale changes
  };
  const textStyle = normalizeTextStyle(
    StyleSheet.flatten([baseTextStyle, fontStyle, colorStyle, props.sx]),
  );

  // On Android, selectable defaults to false if not specified. For all other platforms selectable defaults to true.
  // This is a workaround for an Android bug where setting selectable implicitly also makes the text focusable, adding it to the keyboard focus order.
  // See A11Y-7173 for additional details (https://indeed.atlassian.net/browse/A11Y-7173).
  const selectable =
    Platform.OS === "android" ? props.selectable === true : props.selectable !== false;

  return (
    <RNText
      ref={props.ref}
      testID={props.testID}
      // @ts-expect-error dir is a react-native-web prop not in RN type definitions
      dir={typography.dir}
      style={textStyle}
      aria-label={props.ariaLabel}
      aria-level={props.ariaLevel}
      accessibilityRole={props.accessibilityRole}
      selectable={selectable}
      {...(props.ariaHidden === true ? { "aria-hidden": true } : {})}
      onPress={props.onPress}
      nativeID={props.nativeID}
      accessible={props.accessible}
      numberOfLines={props.numberOfLines}
      ellipsizeMode="tail"
      onTextLayout={props.onTextLayout}
    >
      <TextAncestorContext.Provider value={true}>{props.children}</TextAncestorContext.Provider>
    </RNText>
  );
}

export const TEXT_VARIANTS: TextVariant[] = ["normal", "contrast", "secondary", "alert", "success"];

// Export FONT_FAMILY for backward compatibility
export const FONT_FAMILY = "System"; // This will be overridden by theme.fonts.default
